import { 
  PackagePlusIcon as Package,
  ClockIcon as Clock,
  CheckCircle2Icon as CheckCircle2,
  DollarSignIcon as DollarSign,
  Target as Target,
  ArrowRightIcon as TrendingUp,
  ArrowRightIcon as ArrowDownRight,
  XIcon as X
} from './icons';
import { ShieldAlert } from 'lucide-react';
import { usePermissions } from '../contexts/PermissionContext';
import { useUnifiedFeaturePermission } from '../hooks/useUnifiedPermissions';

interface ReportsProps {
  onClose: () => void;
}

// Mock data - In production, this would come from your backend
const mockData = {
  orders: {
    total: 150,
    pending: 45,
    delivered: 95,
    trend: 12
  },
  payments: {
    pending: 'R$ 12.500,00',
    commission: 'R$ 3.750,00',
    trend: -5
  },
  goals: {
    current: 85,
    target: 'R$ 50.000,00',
    remaining: 'R$ 7.500,00'
  }
};

export function Reports({ onClose }: ReportsProps) {
  const canViewReports = useUnifiedFeaturePermission('general.can_view_reports');

  // 🛡️ SEGURANÇA: Validação interna de permissão
  if (!canViewReports) {
    return (
      <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center">
        <div className="w-[400px] bg-navy-800 rounded-lg shadow-xl p-6 text-center">
          <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-white mb-2">Acesso Negado</h2>
          <p className="text-gray-400 mb-4">Você não tem permissão para acessar relatórios.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-navy-700 text-white rounded hover:bg-navy-600 transition-colors"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center">
      <div className="w-[400px] bg-navy-800 rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-navy-700">
          <h2 className="text-lg font-bold text-white">Dashboard de Vendas</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-navy-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-emerald-400">+{mockData.orders.trend}%</span>
              </div>
              <div className="text-xs text-gray-400">Total</div>
              <div className="text-lg font-bold text-white">{mockData.orders.total}</div>
            </div>

            <div className="bg-navy-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-4 h-4 text-orange-400" />
                <span className="text-xs text-orange-400">{mockData.orders.pending}</span>
              </div>
              <div className="text-xs text-gray-400">Pendentes</div>
              <div className="text-lg font-bold text-white">{mockData.payments.pending}</div>
            </div>

            <div className="bg-navy-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400">{mockData.orders.delivered}</span>
              </div>
              <div className="text-xs text-gray-400">Entregues</div>
              <div className="text-lg font-bold text-white">{mockData.orders.delivered}</div>
            </div>
          </div>

          {/* Commission */}
          <div className="bg-navy-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <DollarSign className="w-4 h-4 text-yellow-400 mr-2" />
                <span className="text-sm text-gray-400">Comissão do Período</span>
              </div>
              <div className="flex items-center text-xs">
                <ArrowDownRight className="w-3 h-3 text-red-400 mr-1" />
                <span className="text-red-400">{mockData.payments.trend}%</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-white font-medium">{mockData.payments.commission}</span>
              <span className="text-xs text-gray-400">Este mês</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white">R$ 1.250,00</span>
              <span className="text-xs text-gray-400">Última semana</span>
            </div>
          </div>

          {/* Goals */}
          <div className="bg-navy-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Target className="w-4 h-4 text-purple-400 mr-2" />
                <span className="text-sm text-gray-400">Meta do Mês</span>
              </div>
              <div className="flex items-center text-xs">
                <TrendingUp className="w-3 h-3 text-emerald-400 mr-1" />
                <span className="text-emerald-400">{mockData.goals.current}%</span>
              </div>
            </div>
            <div className="text-sm text-white font-medium mb-2">{mockData.goals.target}</div>
            <div className="space-y-2">
              <div className="w-full bg-navy-800 rounded-full h-1.5">
                <div 
                  className="bg-purple-500 h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${mockData.goals.current}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Faltam</span>
                <span className="text-white">{mockData.goals.remaining}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-gray-500 p-3 border-t border-navy-700">
          Atualizado em {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}